import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiSettings, 
  FiPlus, 
  FiChevronDown,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiX,
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiAlertTriangle,
  FiFileText,
  FiLock,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiCalendar,
  FiTag,
  FiUpload,
  FiFile,
  FiTrash2,
  FiActivity,
  FiLayers,
  FiDatabase
} from 'react-icons/fi';

// Import the molecular medicine service
import molecularMedicineService from '../../../PatientTreatmentDetails/services/molecularMedicineService'; // Adjust path as needed
import { API_DOCTOR_LIST } from '../../../../helpers/urlHelper';
// Constants
const CONSTANTS = {
  STUDY_TYPES: [
    'Análisis Genético',
    'Secuenciación del Genoma',
    'Prueba de Farmacogenómica',
    'Análisis de Mutaciones',
    'Perfil Molecular del Cáncer',
    'Diagnóstico Molecular',
    'Análisis de Microbioma',
    'Biomarcadores Moleculares',
    'Terapia Génica',
    'Medicina de Precisión',
    'Análisis de Expresión Génica',
    'Proteómica',
    'Metabolómica'
  ],
  SAMPLE_TYPES: [
    'Sangre', 'Saliva', 'Tejido', 'Biopsia', 'Orina', 'Líquido Cefalorraquídeo',
    'Médula Ósea', 'Líquido Amniótico', 'Células Bucales', 'Plasma', 'Suero',
    'ADN', 'ARN', 'Proteínas'
  ],
  CLINICAL_AREAS: [
    'Oncología', 'Cardiología', 'Neurología', 'Endocrinología', 'Inmunología',
    'Hematología', 'Genética Médica', 'Farmacología', 'Enfermedades Raras',
    'Medicina Reproductiva', 'Pediatría', 'Psiquiatría', 'Infectología'
  ],
  PRIORITIES: ['Low', 'Medium', 'High', 'Urgent', 'STAT'],
  STATUSES: ['Sample Collection', 'Processing', 'Analysis', 'Results Available', 'Reported'],
  SEQUENCING_PLATFORMS: ['Illumina', 'Ion Torrent', 'Oxford Nanopore', 'PacBio', 'Sanger', 'Other'],
  MAX_FILES: 10
};

// Default form state
const DEFAULT_STUDY_STATE = {
  patientId: '',
  name: '',
  type: '',
  sampleType: '',
  clinicalArea: '',
  genesPanels: [],
  status: 'Sample Collection',
  priority: 'Medium',
  consent: false,
  geneticCounseling: false,
  notes: '',
  clinicalIndication: '',
  sampleCollectionDate: '',
  doctorId: '',
  sequencingPlatform: '',
  coverage: ''
};

const DEFAULT_FILTERS_STATE = {
  type: '',
  sampleType: '',
  clinicalArea: '',
  status: 'All',
  priority: 'All',
  lastUpdate: '',
  startDate: '',
  endDate: ''
};

// CAMBIO 1: Agregar onCountUpdate y componentName a los props
const MolecularMedicineTreatments = ({ patientId, onCountUpdate, componentName }) => {
  // State management
  const [molecularStudies, setMolecularStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [loading, setLoading] = useState({
    studies: false,
    doctors: false,
    submitting: false
  });
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [doctors, setDoctors] = useState([]);
  
  // Offcanvas states
  const [offcanvas, setOffcanvas] = useState({
    add: false,
    view: false,
    filter: false
  });
  
  // Dropdown states
  const [dropdowns, setDropdowns] = useState({
    mostRecent: false
  });
  
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS_STATE);
  const [newStudy, setNewStudy] = useState({
    ...DEFAULT_STUDY_STATE,
    patientId: patientId
  });

  // Utility functions
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  }, []);

  const getStatusConfig = useCallback((status) => {
    const configs = {
      'Reported': { color: 'success', icon: <FiCheck /> },
      'Results Available': { color: 'info', icon: <FiDatabase /> },
      'Analysis': { color: 'warning', icon: <FiActivity /> },
      'Processing': { color: 'primary', icon: <FiLayers /> },
      'Sample Collection': { color: 'secondary', icon: <FiClock /> }
    };
    return configs[status] || { color: 'secondary', icon: <FiSquare /> };
  }, []);

  const getPriorityColor = useCallback((priority) => {
    const colors = {
      'STAT': 'danger',
      'Urgent': 'danger',
      'High': 'warning',
      'Medium': 'info',
      'Low': 'secondary'
    };
    return colors[priority] || 'secondary';
  }, []);

  const getFileIcon = useCallback((fileType) => {
    const configs = {
      'pdf': { icon: <FiFileText />, color: 'danger' },
      'vcf': { icon: <FiDatabase />, color: 'primary' },
      'bam': { icon: <FiDatabase />, color: 'primary' },
      'fastq': { icon: <FiDatabase />, color: 'primary' },
      'xlsx': { icon: <FiFile />, color: 'success' },
      'xls': { icon: <FiFile />, color: 'success' },
      'jpg': { icon: <FiFile />, color: 'info' },
      'jpeg': { icon: <FiFile />, color: 'info' },
      'png': { icon: <FiFile />, color: 'info' }
    };
    return configs[fileType?.toLowerCase()] || { icon: <FiFile />, color: 'secondary' };
  }, []);

  // API functions - Updated fetchDoctors
  const fetchDoctors = useCallback(async () => {
    setLoading(prev => ({ ...prev, doctors: true }));
    try {
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error('Failed to fetch doctors');
      }
      const doctorsData = await doctorsResponse.json();
      
      console.log('Raw doctors response:', doctorsData);
      
      const doctorsArray = doctorsData && doctorsData.data ? doctorsData.data : [];
      
      console.log('Processed doctors array:', doctorsArray);
      setDoctors(doctorsArray);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      showNotification('Error loading doctors', 'error');
      setDoctors([]);
    } finally {
      setLoading(prev => ({ ...prev, doctors: false }));
    }
  }, []);

  // CAMBIO 2: Actualizar loadMolecularStudies con contador
  const loadMolecularStudies = useCallback(async () => {
    if (!patientId) {
      console.warn('No patientId provided to MolecularMedicineTreatments');
      setMolecularStudies([]);
      // Actualizar contador cuando no hay patientId
      if (onCountUpdate && componentName) {
        onCountUpdate(componentName, 0);
      }
      return;
    }

    setLoading(prev => ({ ...prev, studies: true }));
    try {
      const response = await molecularMedicineService.getStudiesByPatient(patientId);
      const studies = response.data || [];
      
      // IMPORTANTE: Solo actualizar si los datos han cambiado
      setMolecularStudies(prevStudies => {
        if (JSON.stringify(prevStudies) !== JSON.stringify(studies)) {
          // Solo llamar onCountUpdate cuando los datos cambien
          if (onCountUpdate && componentName) {
            onCountUpdate(componentName, studies.length);
          }
          return studies;
        }
        return prevStudies;
      });
    } catch (error) {
      console.error('Error loading molecular studies:', error);
      showNotification(`Could not load molecular medicine studies: ${error.message}`, 'error');
      setMolecularStudies([]);
      // También actualizar contador en caso de error
      if (onCountUpdate && componentName) {
        onCountUpdate(componentName, 0);
      }
    } finally {
      setLoading(prev => ({ ...prev, studies: false }));
    }
  }, [patientId, onCountUpdate, componentName]);

  // Filter functions
  const applyFilters = useCallback(() => {
    let filtered = [...molecularStudies];

    // Global search filter
    if (globalFilter) {
      const searchTerm = globalFilter.toLowerCase();
      filtered = filtered.filter(study => 
        study.name.toLowerCase().includes(searchTerm) ||
        study.type.toLowerCase().includes(searchTerm) ||
        (study.clinicalIndication && study.clinicalIndication.toLowerCase().includes(searchTerm))
      );
    }

    // Apply specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value === 'All') return;
      
      switch (key) {
        case 'type':
        case 'sampleType':
        case 'clinicalArea':
        case 'status':
        case 'priority':
          filtered = filtered.filter(study => study[key] === value);
          break;
        case 'startDate':
          filtered = filtered.filter(study => {
            const studyDate = new Date(study.lastUpdate);
            return studyDate >= new Date(value);
          });
          break;
        case 'endDate':
          filtered = filtered.filter(study => {
            const studyDate = new Date(study.lastUpdate);
            return studyDate <= new Date(value);
          });
          break;
      }
    });

    setFilteredStudies(filtered);
  }, [molecularStudies, globalFilter, filters]);

  const getActiveFilters = useCallback(() => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({ key: 'search', label: `Search: "${globalFilter}"` });
    }
    
    const filterLabels = {
      type: 'Type',
      sampleType: 'Sample',
      clinicalArea: 'Area',
      status: 'Status',
      priority: 'Priority',
      startDate: 'From',
      endDate: 'To'
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All') {
        const label = filterLabels[key];
        const displayValue = ['startDate', 'endDate'].includes(key) 
          ? new Date(value).toLocaleDateString() 
          : value;
        activeFilters.push({ key, label: `${label}: ${displayValue}` });
      }
    });
    
    return activeFilters;
  }, [globalFilter, filters]);

  // Offcanvas and UI handlers
  const toggleOffcanvas = useCallback((offcanvasName, state = null) => {
    setOffcanvas(prev => ({
      ...prev,
      [offcanvasName]: state !== null ? state : !prev[offcanvasName]
    }));
  }, []);

  const toggleDropdown = useCallback((dropdownName, state = null) => {
    setDropdowns(prev => ({
      ...prev,
      [dropdownName]: state !== null ? state : !prev[dropdownName]
    }));
  }, []);

  const showNotification = useCallback((message, type = 'info') => {
    if (window.Swal) {
      window.Swal.fire({
        icon: type === 'error' ? 'error' : type === 'success' ? 'success' : 'info',
        title: type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Information',
        text: message,
        timer: type === 'success' ? 3000 : 5000,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    } else {
      alert(message);
    }
  }, []);

  // Form handlers
  const handleFormChange = useCallback((field, value) => {
    setNewStudy(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  const removeFilter = useCallback((filterKey) => {
    if (filterKey === 'search') {
      setGlobalFilter('');
    } else {
      const resetValue = ['status', 'priority'].includes(filterKey) ? 'All' : '';
      handleFilterChange(filterKey, resetValue);
    }
  }, [handleFilterChange]);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS_STATE);
    setGlobalFilter('');
  }, []);

  // Updated doctor selection handler
  const handleDoctorSelection = useCallback((doctorId) => {
    console.log('Doctor seleccionado:', doctorId);
    console.log('Lista de doctores disponibles:', doctors);
    
    const selectedDoctor = doctors.find(d => d._id === doctorId);
    console.log('Doctor encontrado:', selectedDoctor);
    
    if (selectedDoctor) {
      setNewStudy(prev => {
        const updated = {
          ...prev,
          doctorId: selectedDoctor._id
        };
        console.log('Estado actualizado newStudy.doctorId:', updated.doctorId);
        return updated;
      });
    } else if (doctorId === '') {
      // Caso cuando se deselecciona el doctor
      setNewStudy(prev => ({
        ...prev,
        doctorId: ''
      }));
      console.log('Doctor deseleccionado, doctorId limpiado');
    } else {
      console.warn('Doctor no encontrado para ID:', doctorId);
    }
  }, [doctors]);

  // File handling
  const handleFileUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length + uploadedFiles.length > CONSTANTS.MAX_FILES) {
      showNotification(`Maximum ${CONSTANTS.MAX_FILES} files allowed`, 'error');
      return;
    }
    setUploadedFiles(prev => [...prev, ...files]);
  }, [uploadedFiles.length, showNotification]);

  const removeUploadedFile = useCallback((index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Study actions
  const handleViewStudy = useCallback(async (studyId) => {
    try {
      const response = await molecularMedicineService.getMolecularMedicineStudyById(studyId);
      setSelectedStudy(response.data);
      toggleOffcanvas('view', true);
    } catch (error) {
      console.error('Error loading study details:', error);
      showNotification(`Error loading study details: ${error.message}`, 'error');
    }
  }, [toggleOffcanvas, showNotification]);

  const handleAddNew = useCallback(() => {
    setNewStudy({ ...DEFAULT_STUDY_STATE, patientId });
    setUploadedFiles([]);
    toggleOffcanvas('add', true);
  }, [patientId, toggleOffcanvas]);

  // CAMBIO 3: Actualizar handleSubmitNewStudy para recargar datos después de crear
  const handleSubmitNewStudy = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading.submitting) return;
    setLoading(prev => ({ ...prev, submitting: true }));
    
    try {
      // Validation
      const requiredFields = {
        name: 'Study Name',
        type: 'Study Type',
        sampleType: 'Sample Type',
        clinicalIndication: 'Clinical Indication',
        patientId: 'Patient ID'
      };

      const missingFields = Object.keys(requiredFields).filter(field => 
        !newStudy[field] || newStudy[field].toString().trim() === ''
      );

      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(field => requiredFields[field]);
        throw new Error(`Please fill in the following required fields: ${missingFieldNames.join(', ')}`);
      }

      // Prepare submission data
      const submissionData = {
        ...newStudy,
        name: newStudy.name.trim(),
        clinicalIndication: newStudy.clinicalIndication.trim(),
        notes: newStudy.notes?.trim() || ''
      };

      // Create FormData with files
      const formData = molecularMedicineService.createFormDataWithFiles(submissionData, uploadedFiles);
      
      const result = await molecularMedicineService.createMolecularMedicineStudy(formData);
      
      if (result.success !== false) {
        // Recargar datos después de crear exitosamente - esto actualizará el contador
        await loadMolecularStudies();
        toggleOffcanvas('add', false);
        setUploadedFiles([]);
        showNotification(result.message || 'Molecular medicine study created successfully!', 'success');
      } else {
        throw new Error(result.message || 'Failed to create study');
      }
    } catch (error) {
      console.error('Error creating molecular medicine study:', error);
      const errorDetails = molecularMedicineService.handleApiError(error);
      showNotification(`Error creating study: ${errorDetails.message}`, 'error');
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  }, [loading.submitting, newStudy, uploadedFiles, loadMolecularStudies, toggleOffcanvas, showNotification]);

  // CAMBIO 4: Actualizar handleDeleteStudy para recargar datos después de eliminar
  const handleDeleteStudy = useCallback(async (studyId) => {
    if (!window.confirm('Are you sure you want to delete this study?')) return;
    
    try {
      const result = await molecularMedicineService.deleteMolecularMedicineStudy(studyId);
      
      if (result.success !== false) {
        // Recargar datos después de eliminar exitosamente - esto actualizará el contador
        await loadMolecularStudies();
        toggleOffcanvas('view', false);
        showNotification('Study deleted successfully!', 'success');
      } else {
        throw new Error(result.message || 'Failed to delete study');
      }
    } catch (error) {
      console.error('Error deleting study:', error);
      const errorDetails = molecularMedicineService.handleApiError(error);
      showNotification(`Error deleting study: ${errorDetails.message}`, 'error');
    }
  }, [loadMolecularStudies, toggleOffcanvas, showNotification]);

  const handleMostRecentAction = useCallback((action) => {
    toggleDropdown('mostRecent', false);
    switch (action) {
      case 'sendMail':
        console.log('Send mail action');
        break;
      case 'sendMessage':
        console.log('Send message action');
        break;
      case 'downloadReport':
        console.log('Download report action');
        break;
      default:
        break;
    }
  }, [toggleDropdown]);

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      Cell: ({ cell, row }) => (
        <div>
          <strong>{cell.getValue()}</strong>
          <div className="text-muted small">
            {row.original.type}
            {row.original.sampleType && ` - ${row.original.sampleType}`}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'clinicalArea',
      header: 'Clinical Area',
      size: 150,
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'lastUpdate',
      header: 'Date',
      size: 120,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 150,
      Cell: ({ cell }) => {
        const status = cell.getValue();
        const config = getStatusConfig(status);
        
        return (
          <span className={`badge bg-${config.color} d-flex align-items-center gap-1`}>
            <span>{config.icon}</span>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      size: 100,
      Cell: ({ cell }) => {
        const priority = cell.getValue();
        const color = getPriorityColor(priority);
        
        return (
          <span className={`badge bg-${color}`}>
            {priority}
          </span>
        );
      },
    },
    {
      accessorKey: '_id',
      header: 'Action',
      size: 100,
      enableSorting: false,
      Cell: ({ cell }) => (
        <button
          className="btn btn-link btn-sm text-primary p-0 d-flex align-items-center gap-1"
          onClick={(e) => {
            e.stopPropagation();
            handleViewStudy(cell.getValue());
          }}
        >
          <FiEye /> View
        </button>
      ),
    },
  ], [formatDate, getStatusConfig, getPriorityColor, handleViewStudy]);

  // Effects
  useEffect(() => {
    if (patientId) {
      loadMolecularStudies();
      fetchDoctors();
    }
  }, [patientId, loadMolecularStudies, fetchDoctors]);

  // CAMBIO 5: Agregar efecto separado para actualizar contador cuando los datos cambien
  useEffect(() => {
    if (onCountUpdate && componentName) {
      onCountUpdate(componentName, molecularStudies.length);
    }
  }, [molecularStudies.length, onCountUpdate, componentName]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setDropdowns(prev => ({ ...prev, mostRecent: false }));
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const activeFilters = getActiveFilters();

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">Molecular Medicine</h6>
      </div>

      <div className="card-body">
        {/* Search and Actions Bar */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex align-items-center gap-2">
              {/* Search Input */}
              <div className="input-group" style={{maxWidth: '300px'}}>
                <span className="input-group-text">
                  <FiSearch />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
              </div>

              {/* Most Recent Dropdown */}
              <div className="dropdown" style={{position: 'relative'}}>
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown('mostRecent');
                  }}
                >
                  Most Recent
                  <FiChevronDown />
                </button>
                {dropdowns.mostRecent && (
                  <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', left: 0, zIndex: 1000}}>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleMostRecentAction('sendMail')}
                    >
                      <FiMail />
                      Send Mail
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleMostRecentAction('sendMessage')}
                    >
                      <FiMessageSquare />
                      Send Message
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center gap-2"
                      onClick={() => handleMostRecentAction('downloadReport')}
                    >
                      <FiDownload />
                      Download Report
                    </button>
                  </div>
                )}
              </div>

              {/* Filters Button */}
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => toggleOffcanvas('filter', true)}
              >
                <FiFilter />
                Filters
                {activeFilters.length > 0 && (
                  <span className="badge bg-primary rounded-pill">
                    {activeFilters.length}
                  </span>
                )}
              </button>

              
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {activeFilters.length > 0 && (
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="text-muted small">Active filters:</span>
              {activeFilters.map((filter, index) => (
                <span
                  key={index}
                  className="badge bg-light text-dark border d-flex align-items-center gap-1"
                >
                  {filter.label}
                  <button
                    type="button"
                    className="btn-close"
                    style={{ fontSize: '0.7em' }}
                    onClick={() => removeFilter(filter.key)}
                  ></button>
                </span>
              ))}
              <button
                className="btn btn-link btn-sm text-decoration-none p-0"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="table-responsive">
          <MaterialReactTable
            columns={columns}
            data={filteredStudies}
            enableRowSelection
            onRowSelectionChange={setRowSelection}
            state={{
              rowSelection,
              isLoading: loading.studies,
              globalFilter
            }}
            enableGlobalFilter={false}
            enableColumnFilters={false}
            enableSorting={true}
            enablePagination={true}
            initialState={{
              pagination: { pageSize: 10 },
              sorting: [{ id: 'lastUpdate', desc: true }]
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleViewStudy(row.original._id),
              style: { cursor: 'pointer' }
            })}
            
          />
        </div>
      </div>

      {/* Filter Offcanvas */}
      <div className={`offcanvas offcanvas-end ${offcanvas.filter ? 'show' : ''}`} 
           style={{ visibility: offcanvas.filter ? 'visible' : 'hidden' }}>
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">Filters</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => toggleOffcanvas('filter', false)}
          ></button>
        </div>
        <div className="offcanvas-body">
          <form>
            <div className="mb-3">
              <label className="form-label">Study Type</label>
              <select
                className="form-select"
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
              >
                <option value="">All Types</option>
                {CONSTANTS.STUDY_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Sample Type</label>
              <select
                className="form-select"
                value={filters.sampleType}
                onChange={(e) => handleFilterChange('sampleType', e.target.value)}
              >
                <option value="">All Sample Types</option>
                {CONSTANTS.SAMPLE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Clinical Area</label>
              <select
                className="form-select"
                value={filters.clinicalArea}
                onChange={(e) => handleFilterChange('clinicalArea', e.target.value)}
              >
                <option value="">All Areas</option>
                {CONSTANTS.CLINICAL_AREAS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="All">All Statuses</option>
                {CONSTANTS.STATUSES.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="All">All Priorities</option>
                {CONSTANTS.PRIORITIES.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary flex-fill"
                onClick={handleClearFilters}
              >
                Clear All
              </button>
              <button
                type="button"
                className="btn btn-primary flex-fill"
                onClick={() => toggleOffcanvas('filter', false)}
              >
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      </div>

      

      {/* View Study Offcanvas */}
<div className={`offcanvas offcanvas-end ${offcanvas.view ? 'show' : ''}`} 
     style={{ visibility: offcanvas.view ? 'visible' : 'hidden', width: '700px' }}>
  <div className="offcanvas-header border-bottom">
    <div className="d-flex flex-column">
      <h5 className="offcanvas-title mb-1">Study Details</h5>
      <small className="text-muted">Molecular Medicine Study Information</small>
    </div>
    <button
      type="button"
      className="btn-close"
      onClick={() => toggleOffcanvas('view', false)}
    ></button>
  </div>
  <div className="offcanvas-body p-0">
    {selectedStudy ? (
      <div className="h-100" style={{ overflowY: 'auto' }}>
        {/* Study Header Card */}
        <div className="card border-0 border-bottom rounded-0">
          <div className="card-body">
            <div className="d-flex align-items-start justify-content-between mb-3">
              <div className="flex-grow-1">
                <h4 className="card-title mb-2 text-primary">{selectedStudy.name}</h4>
                <p className="text-muted mb-0">{selectedStudy.type}</p>
              </div>
              <div className="text-end">
                <div className="mb-2">
                  <span className={`badge bg-${getStatusConfig(selectedStudy.status).color} fs-6 px-3 py-2 d-flex align-items-center gap-2`}>
                    {getStatusConfig(selectedStudy.status).icon}
                    {selectedStudy.status}
                  </span>
                </div>
                <span className={`badge bg-${getPriorityColor(selectedStudy.priority)} fs-6 px-3 py-2`}>
                  <FiAlertTriangle className="me-1" />
                  {selectedStudy.priority} Priority
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Information Card */}
        <div className="card border-0 border-bottom rounded-0">
          <div className="card-header bg-light">
            <h6 className="card-title mb-0 d-flex align-items-center">
              <FiInfo className="me-2 text-primary" />
              Basic Information
            </h6>
          </div>
          <div className="card-body">
            <div className="row g-4">
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-3">
                  <div className="icon-circle bg-primary bg-opacity-10 me-3">
                    <FiLayers className="text-primary" />
                  </div>
                  <div>
                    <small className="text-muted d-block">Study Type</small>
                    <strong>{selectedStudy.type}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-3">
                  <div className="icon-circle bg-success bg-opacity-10 me-3">
                    <FiDatabase className="text-success" />
                  </div>
                  <div>
                    <small className="text-muted d-block">Sample Type</small>
                    <strong>{selectedStudy.sampleType}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-3">
                  <div className="icon-circle bg-info bg-opacity-10 me-3">
                    <FiTag className="text-info" />
                  </div>
                  <div>
                    <small className="text-muted d-block">Clinical Area</small>
                    <strong>{selectedStudy.clinicalArea || 'Not specified'}</strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center mb-3">
                  <div className="icon-circle bg-warning bg-opacity-10 me-3">
                    <FiCalendar className="text-warning" />
                  </div>
                  <div>
                    <small className="text-muted d-block">Last Update</small>
                    <strong>{formatDateTime(selectedStudy.lastUpdate)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Clinical Information Card */}
        <div className="card border-0 border-bottom rounded-0">
          <div className="card-header bg-light">
            <h6 className="card-title mb-0 d-flex align-items-center">
              <FiFileText className="me-2 text-primary" />
              Clinical Information
            </h6>
          </div>
          <div className="card-body">
            <div className="mb-4">
              <label className="form-label text-muted small">Clinical Indication</label>
              <div className="bg-light rounded p-3 border-start border-primary border-4">
                <p className="mb-0">{selectedStudy.clinicalIndication || 'No clinical indication provided'}</p>
              </div>
            </div>
            
            {selectedStudy.notes && (
              <div className="mb-4">
                <label className="form-label text-muted small">Additional Notes</label>
                <div className="bg-light rounded p-3 border-start border-info border-4">
                  <p className="mb-0">{selectedStudy.notes}</p>
                </div>
              </div>
            )}

            {selectedStudy.sampleCollectionDate && (
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="icon-circle bg-primary bg-opacity-10 me-3">
                      <FiCalendar className="text-primary" />
                    </div>
                    <div>
                      <small className="text-muted d-block">Sample Collection Date</small>
                      <strong>{formatDate(selectedStudy.sampleCollectionDate)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Technical Information Card */}
        {(selectedStudy.sequencingPlatform || selectedStudy.coverage) && (
          <div className="card border-0 border-bottom rounded-0">
            <div className="card-header bg-light">
              <h6 className="card-title mb-0 d-flex align-items-center">
                <FiActivity className="me-2 text-primary" />
                Technical Specifications
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-4">
                {selectedStudy.sequencingPlatform && (
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="icon-circle bg-success bg-opacity-10 me-3">
                        <FiDatabase className="text-success" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Sequencing Platform</small>
                        <strong>{selectedStudy.sequencingPlatform}</strong>
                      </div>
                    </div>
                  </div>
                )}
                {selectedStudy.coverage && (
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <div className="icon-circle bg-info bg-opacity-10 me-3">
                        <FiLayers className="text-info" />
                      </div>
                      <div>
                        <small className="text-muted d-block">Coverage</small>
                        <strong>{selectedStudy.coverage}</strong>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Consent & Compliance Card */}
        <div className="card border-0 border-bottom rounded-0">
          <div className="card-header bg-light">
            <h6 className="card-title mb-0 d-flex align-items-center">
              <FiLock className="me-2 text-primary" />
              Consent & Compliance
            </h6>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center p-3 rounded border">
                  <div className={`icon-circle ${selectedStudy.consent ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'} me-3`}>
                    {selectedStudy.consent ? (
                      <FiCheck className="text-success" />
                    ) : (
                      <FiX className="text-danger" />
                    )}
                  </div>
                  <div>
                    <strong className={selectedStudy.consent ? 'text-success' : 'text-danger'}>
                      Patient Consent
                    </strong>
                    <small className="d-block text-muted">
                      {selectedStudy.consent ? 'Obtained and documented' : 'Not obtained'}
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center p-3 rounded border">
                  <div className={`icon-circle ${selectedStudy.geneticCounseling ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'} me-3`}>
                    {selectedStudy.geneticCounseling ? (
                      <FiCheck className="text-success" />
                    ) : (
                      <FiX className="text-danger" />
                    )}
                  </div>
                  <div>
                    <strong className={selectedStudy.geneticCounseling ? 'text-success' : 'text-danger'}>
                      Genetic Counseling
                    </strong>
                    <small className="d-block text-muted">
                      {selectedStudy.geneticCounseling ? 'Provided to patient' : 'Not provided'}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Attachments Card */}
        {selectedStudy.files && selectedStudy.files.length > 0 && (
          <div className="card border-0 border-bottom rounded-0">
            <div className="card-header bg-light">
              <h6 className="card-title mb-0 d-flex align-items-center justify-content-between">
                <span>
                  <FiFile className="me-2 text-primary" />
                  Attachments
                </span>
                <span className="badge bg-primary rounded-pill">
                  {selectedStudy.files.length}
                </span>
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {selectedStudy.files.map((file, index) => {
                  const fileExtension = file.filename ? file.filename.split('.').pop() : 'file';
                  const fileConfig = getFileIcon(fileExtension);
                  return (
                    <div key={index} className="col-md-6">
                      <div className="d-flex align-items-center p-3 border rounded hover-shadow transition-all">
                        <div className={`icon-circle bg-${fileConfig.color} bg-opacity-10 me-3`}>
                          <span className={`text-${fileConfig.color}`}>
                            {fileConfig.icon}
                          </span>
                        </div>
                        <div className="flex-grow-1 me-2">
                          <div className="fw-medium text-truncate" title={file.filename || `File ${index + 1}`}>
                            {file.filename || `File ${index + 1}`}
                          </div>
                          <small className="text-muted">
                            {fileExtension ? fileExtension.toUpperCase() : 'FILE'}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => window.open(file.url, '_blank')}
                          title="Download file"
                        >
                          <FiDownload />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        
      </div>
    ) : (
      <div className="d-flex align-items-center justify-content-center h-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading study details...</p>
        </div>
      </div>
    )}
  </div>
</div>

{/* Add this CSS to your component or global styles */}
<style jsx>{`
  .icon-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .hover-shadow {
    transition: all 0.2s ease;
  }
  
  .hover-shadow:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    transform: translateY(-1px);
  }
  
  .transition-all {
    transition: all 0.2s ease;
  }
  
  .border-start {
    border-left-width: 4px !important;
  }
  
  .offcanvas-body::-webkit-scrollbar {
    width: 6px;
  }
  
  .offcanvas-body::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  .offcanvas-body::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  .offcanvas-body::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`}</style>

      {/* Backdrop for offcanvas */}
      {(offcanvas.add || offcanvas.view || offcanvas.filter) && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={() => {
            setOffcanvas({ add: false, view: false, filter: false });
          }}
        ></div>
      )}
    </div>
  );
};

export default MolecularMedicineTreatments;